/**
 * RatingScale — 1-5 or 1-10 button stripe.
 *
 * UX V2 (Feb 2026 refinement):
 *   - Labels rendered ABOVE the scale (Låg ←→ Hög feel) — faster scan.
 *   - 1-10 scales now render as 2 rows of 5 buttons (cleaner mobile rhythm).
 *   - Numbers, not stars. No talent connotation per RATING PHILOSOPHY §1.
 *   - Status questions use cool palette; development uses warm.
 *   - `inverted` flips visual color cue for fatigue/pain (high = worse).
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { localizedScaleLabels } from "../../lib/evaluationScaleLabels";

export default function RatingScale({
  max = 10,
  value,
  onChange,
  qid,
  inverted = false,
  sectionType = "development",
  isCoach = false,
  scaleLabels,
  definitionSource,
}) {
  const { i18n } = useTranslation();
  const labels = localizedScaleLabels(scaleLabels, i18n.language, definitionSource);
  const english = (i18n.language || "sv").startsWith("en");
  const items = Array.from({ length: max }, (_, i) => i + 1);

  const isStatus = sectionType === "status";
  const baseUnselected =
    "bg-white border border-[rgb(var(--tn-border-e4e4e7))] text-[rgb(var(--tn-text-71717a))] hover:border-[rgb(var(--tn-border-a1a1aa))] dark:bg-[rgb(var(--tn-bg-18181b))] dark:border-[rgb(var(--tn-border-27272a))] dark:text-[rgb(var(--tn-text-a1a1aa))]";

  // 2-row layout for 10-scale; single row for 5-scale.
  const cols = "grid-cols-5";
  const rows = max === 10 ? [items.slice(0, 5), items.slice(5)] : [items];

  const renderButton = (n) => {
    const active = value === n;
    const colorClass = active
      ? isStatus
        ? inverted
          ? "bg-amber-500 border-amber-500 text-white shadow-[0_4px_16px_rgba(245,158,11,0.25)]"
          : "bg-sky-600 border-sky-600 text-[rgb(var(--tn-text-ffffff))] shadow-[0_4px_16px_rgba(2,132,199,0.25)]"
        : "bg-[rgb(var(--tn-bg-18181b))] border-[rgb(var(--tn-border-18181b))] text-[rgb(var(--tn-text-ffffff))] dark:bg-white dark:text-black dark:border-[rgb(var(--tn-border-ffffff))] shadow-[0_4px_16px_rgba(0,0,0,0.15)]"
      : baseUnselected;
    return (
      <button
        key={n}
        type="button"
        data-testid={`rating-${qid}-${n}`}
        aria-label={english ? `Value ${n} of ${max}` : `Värde ${n} av ${max}`}
        aria-pressed={active}
        onClick={() => onChange(n)}
        className={`h-12 ${
          max === 5 ? "rounded-2xl" : "rounded-xl"
        } text-sm font-semibold transition-all duration-150 active:scale-[0.96] ${colorClass}`}
      >
        {n}
      </button>
    );
  };

  return (
    <div data-testid={`rating-scale-${qid}`} className="w-full">
      <ScaleLegend inverted={inverted} isStatus={isStatus} labels={labels} english={english} frozen={definitionSource === "frozen"} />
      <div className="space-y-1.5">
        {rows.map((row, i) => (
          <div key={i} className={`grid ${cols} gap-1.5`}>
            {row.map(renderButton)}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScaleLegend({ inverted, isStatus, labels, english, frozen }) {
  // Labels ABOVE the scale per Feb 2026 UX refinement.
  // Frequency-anchored micro-labels per RATING PHILOSOPHY §1.
  let leftLabel = "Låg";
  let rightLabel = "Hög";

  if (isStatus && inverted) {
    leftLabel = "Inget alls";
    rightLabel = "Mycket";
  } else if (isStatus) {
    leftLabel = "Låg";
    rightLabel = "Hög";
  } else {
    leftLabel = "Sällan";
    rightLabel = "Ofta";
  }

  const fallback = english
    ? (isStatus ? (inverted ? ["Not at all", "Very much"] : ["Low", "High"]) : ["Rarely", "Often"])
    : [leftLabel, rightLabel];
  leftLabel = labels?.left || (frozen ? "" : fallback[0]);
  rightLabel = labels?.right || (frozen ? "" : fallback[1]);
  return (
    <div className="mb-2 flex justify-between text-[11px] uppercase tracking-wide text-[rgb(var(--tn-text-a1a1aa))] dark:text-[rgb(var(--tn-text-71717a))] px-1 font-medium">
      <span>{leftLabel}</span>
      <span>{rightLabel}</span>
    </div>
  );
}
